import { resourceGroupZodObject } from "@@/shared/acquire/resources/resource-group";
import { resourceLocalZodSchema } from "@@/shared/acquire/resources/resource-local";
import { resourceSharedZodSchema } from "@@/shared/acquire/resources/resource-shared";
import type { Unit} from "@@/shared/units-and-quantities/unit";
import { unitZodSchema } from "@@/shared/units-and-quantities/unit";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    IsoAndUnixTimestamp} from "../../date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "../../date-time/iso-and-unix";
import type {
    MetaContent} from "../../meta-content/meta-content";
import {
    metaContentZodSchema,
} from "../../meta-content/meta-content";
import { bookableOptionZodSchema } from "../resource-reference-options/bookable-option-zod-schemas";
import type {
    ResourceReference} from "./resource-reference";
import {
    resourcePickerEnabledZodSchema,
} from "./resource-reference";
import type {
    ResourceReferenceId} from "./resource-reference-id";
import {
    resourceReferenceIdFactory,
    resourceReferenceIdZodSchema,
} from "./resource-reference-id";
import { resourceScheduleZodSchema } from "./schedule/resource-schedule";

type RReferences = ResourceReference | ResourceReferenceGroup;

type ResourceReferenceGroup = {
    readonly _id: ResourceReferenceId;
    readonly _type: "RESOURCE_REFERENCE_GROUP";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly meta: MetaContent;
    readonly unit?: Unit;
    readonly references: ResourceReference[];
};

const resourceReferenceZodSchema: z.ZodSchema<ResourceReference> = z.object({
    _id: resourceReferenceIdZodSchema,
    _type: z.literal("RESOURCE_REFERENCE"),
    _createdAt: isoAndUnixZodSchema,
    meta: metaContentZodSchema,
    resource: z.union([
        resourceLocalZodSchema,
        resourceSharedZodSchema,
        resourceGroupZodObject,
    ]),
    options: z.array(bookableOptionZodSchema).min(1),
    resourcePickerEnabled: resourcePickerEnabledZodSchema.optional(),
    schedulingResource: resourceScheduleZodSchema.optional(),
});

const resourceReferenceGroupZodSchema: z.ZodSchema<ResourceReferenceGroup> =
    z.object({
        _id: resourceReferenceIdZodSchema,
        _type: z.literal("RESOURCE_REFERENCE_GROUP"),
        _createdAt: isoAndUnixZodSchema,
        meta: metaContentZodSchema,
        references: z.array(resourceReferenceZodSchema),
        unit: unitZodSchema.optional(),
    });

const resourceReferenceGroupFactory = (
    params: Except<ResourceReferenceGroup, "_id" | "_type" | "_createdAt"> & {
        _id?: ResourceReferenceId;
    },
): ResourceReferenceGroup => {
    return {
        ...params,
        _id: params._id ?? resourceReferenceIdFactory(),
        _type: "RESOURCE_REFERENCE_GROUP",
        _createdAt: nowIsoAndUnix(),
    };
};

const isResourceReferenceGroup = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    input: any,
): input is ResourceReferenceGroup => {
    return input?._type === "RESOURCE_REFERENCE_GROUP";
};

const rReferencesZodSchema: z.ZodSchema<RReferences> = z.union([
    resourceReferenceZodSchema,
    resourceReferenceGroupZodSchema,
]);

export {
    isResourceReferenceGroup,
    rReferencesZodSchema,
    resourceReferenceGroupFactory,
    resourceReferenceGroupZodSchema,
};
export type { RReferences, ResourceReferenceGroup };
